import { useEffect, useState } from "react";

import type { CustomerDetailState, CustomersListState } from "../../../components/authenticated/CustomersWorkspace";
import type { AuthenticatedShellSection } from "../../../components/authenticated/AuthenticatedAppShell";
import type { ApiClient } from "../../../infrastructure/http/apiClient";
import {
  buildCustomerHistoryWindow,
  loadCustomerAppointmentHistory,
  loadCustomerDetail,
  loadCustomerNotes,
  loadCustomers,
  type Customer,
  type CustomerAppointmentHistory,
  type CustomerNote,
  type PaginatedCustomerResponse
} from "../../customers";
import { toCustomerDetailError, toCustomersListError } from "../domain/errors";
import type { AuthenticatedShellPageProps, ShellState } from "../domain/shellTypes";

export function useCustomersSurface({
  activeSection,
  apiClient,
  initialCustomerDetail,
  initialCustomerHistory,
  initialCustomerNotes,
  initialCustomers,
  initialCustomersError,
  isProfessionalUser,
  sessionToken,
  state
}: Pick<AuthenticatedShellPageProps, "initialCustomerDetail" | "initialCustomerHistory" | "initialCustomerNotes" | "initialCustomers" | "initialCustomersError"> & {
  activeSection: AuthenticatedShellSection;
  apiClient: ApiClient;
  isProfessionalUser: boolean;
  sessionToken: string;
  state: ShellState;
}) {
  const [customersState, setCustomersState] = useState<CustomersListState>(() => {
    if (initialCustomers) {
      return { status: "success", result: initialCustomers };
    }

    if (initialCustomersError) {
      return { status: "error", title: initialCustomersError.title, message: initialCustomersError.message, retryable: true };
    }

    return { status: "idle" };
  });
  const [selectedCustomerID, setSelectedCustomerID] = useState(initialCustomerDetail?.id ?? initialCustomers?.data[0]?.id ?? "");
  const [customerDetailState, setCustomerDetailState] = useState<CustomerDetailState>(() => {
    if (initialCustomerDetail && initialCustomerNotes && initialCustomerHistory) {
      return { status: "success", customer: initialCustomerDetail, notes: initialCustomerNotes, history: initialCustomerHistory };
    }

    return { status: "idle" };
  });

  useEffect(() => {
    if (activeSection !== "customers" || state.status !== "success" || isProfessionalUser || !sessionToken || customersState.status !== "idle") {
      return;
    }

    setCustomersState({ status: "loading" });
    loadCustomers(apiClient)
      .then((result) => {
        setCustomersState({ status: "success", result });
        if (!selectedCustomerID && result.data[0]) {
          setSelectedCustomerID(result.data[0].id);
        }
      })
      .catch((error) => {
        setCustomersState(toCustomersListError(error));
      });
  }, [activeSection, apiClient, customersState.status, isProfessionalUser, selectedCustomerID, sessionToken, state.status]);

  useEffect(() => {
    if (customersState.status !== "success" || selectedCustomerID || customersState.result.data.length === 0) {
      return;
    }

    setSelectedCustomerID(customersState.result.data[0].id);
  }, [customersState, selectedCustomerID]);

  useEffect(() => {
    if (activeSection !== "customers" || isProfessionalUser || !selectedCustomerID || !sessionToken) {
      return;
    }

    if (customerDetailState.status === "success" && customerDetailState.customer.id === selectedCustomerID) {
      return;
    }

    if (customerDetailState.status === "loading" && customerDetailState.customerID === selectedCustomerID) {
      return;
    }

    if (customerDetailState.status === "error" && customerDetailState.customerID === selectedCustomerID) {
      return;
    }

    setCustomerDetailState({ status: "loading", customerID: selectedCustomerID });
    loadCustomerWorkspace(apiClient, selectedCustomerID)
      .then((detail) => {
        setCustomerDetailState({ status: "success", ...detail });
      })
      .catch((error) => {
        setCustomerDetailState(toCustomerDetailError(error, selectedCustomerID));
      });
  }, [activeSection, apiClient, customerDetailState, isProfessionalUser, selectedCustomerID, sessionToken]);

  function resetCustomers() {
    setCustomersState({ status: "idle" });
    setSelectedCustomerID("");
    setCustomerDetailState({ status: "idle" });
  }

  return { customerDetailState, customersState, resetCustomers, selectedCustomerID };
}

async function loadCustomerWorkspace(apiClient: ApiClient, customerID: string): Promise<{
  customer: Customer;
  notes: PaginatedCustomerResponse<CustomerNote>;
  history: PaginatedCustomerResponse<CustomerAppointmentHistory>;
}> {
  const historyWindow = buildCustomerHistoryWindow();
  const [customer, notes, history] = await Promise.all([
    loadCustomerDetail(apiClient, customerID),
    loadCustomerNotes(apiClient, customerID),
    loadCustomerAppointmentHistory(apiClient, customerID, historyWindow)
  ]);

  return { customer, notes, history };
}
